import React, { type MutableRefObject } from 'react';
import { RegisterOptions } from 'react-hook-form';
import {
    type ControlledAutocompleteFormStructure,
    type FormStructure,
} from '@europrocurement/flexy-form';
import { Box, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import ContactChip from '../../Dossiers/components/shared/ContactChip/ContactChip';
import { type ContactItem } from '../../Dossiers/hooks';

type RecipientsInputFormStructureType = {
    contactsRef: MutableRefObject<ContactItem[]>;
    fetchPromiseRef: MutableRefObject<Promise<void> | null>;
    inputName: string;
    inputLabel: string;
    listboxTitle: string;
    placeHolder: string;
    rules?: RegisterOptions;
};

type NonEditableArrayDataType = {
    label: string;
    name: string;
    arrayValues: string[];
};
type NonEditableSingleDataType = {
    label: string;
    name: string;
    value: string;
};
type RecipientDataType = {
    emailAddress: string;
    firstName: string;
    lastName: string;
};
type NonEditableRecipientsDataType = {
    label: string;
    name: string;
    arrayValues: RecipientDataType[];
};

const useMailInputsFormStructure = () => {
    const { palette } = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const createRecipientsInput = ({
        contactsRef,
        fetchPromiseRef,
        inputName = 'recipients',
        inputLabel = 'Envoyer à',
        listboxTitle = 'Contacts',
        placeHolder = 'Rechercher un contact',
        rules,
    }: RecipientsInputFormStructureType) =>
        ({
            type: 'autocomplete',
            name: inputName,
            inputlabel: inputLabel,
            listboxTitle,
            placeholder: placeHolder,
            freeSolo: true,
            infos: "Vous pouvez entrer un e-mail libre et l'ajouter via la touche Entrée",
            rules,
            isOnChangeValid: (inputValues, reason, details) => {
                // Reason values -> 'selectOption' (select existing item from list) | 'createOption' (freeSolo manually add item) | 'removeOption' | 'clear'
                // CreatedOption for a freeSolo entry
                if (reason === 'createOption') {
                    const createdValue = details?.option;

                    if (typeof createdValue === 'string') {
                        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createdValue);

                        if (!isEmail) {
                            enqueueSnackbar('E-mail invalide', { variant: 'error' });
                            return { valid: false, validValue: inputValues };
                        }

                        // Remove the new entry
                        inputValues.pop();
                        // The string entry is a valid email, make it looks like a contact
                        inputValues.push({
                            emailAddress: createdValue,
                            fristName: createdValue,
                            lastName: createdValue,
                            id: null,
                        });

                        return { valid: true, validValue: inputValues };
                    }
                    enqueueSnackbar('E-mail invalide', { variant: 'error' });
                    return { valid: false, validValue: inputValues };
                }
                return { valid: true, validValue: inputValues };
            },
            onSearch: async (term): Promise<Array<ContactItem>> => {
                if (fetchPromiseRef.current) {
                    await fetchPromiseRef.current;
                }
                const lowerTerm = term.toLowerCase();
                return contactsRef.current.filter(
                    (contact) =>
                        contact.firstName?.toLowerCase().includes(lowerTerm) ||
                        contact.lastName?.toLowerCase().includes(lowerTerm) ||
                        contact.emailAddress?.toLowerCase().includes(lowerTerm),
                );
            },
            getOptionValue: (option: ContactItem | string) => {
                if (typeof option === 'string') return option;
                return option.id ?? option.emailAddress;
            },

            getOptionLabel: (option: ContactItem | string) => {
                if (typeof option === 'string') {
                    return <Typography>{option}</Typography>;
                }
                return (
                    <ContactChip
                        firstName={option.firstName}
                        lastName={option.lastName}
                        tooltipTitle={option.emailAddress}
                    />
                );
            },
        }) as ControlledAutocompleteFormStructure;

    const createNonEditableRecipientsDataInput = ({
        label,
        name,
        arrayValues,
    }: NonEditableRecipientsDataType) =>
        ({
            type: 'customItem',
            name,
            renderField: () => (
                <Box sx={{ display: 'grid', gap: '4px' }}>
                    <Typography variant="l2dlabel">{label}</Typography>

                    <Box
                        sx={{
                            border: `${palette.grey[300]}  1px solid`,
                            borderRadius: '12px',
                            gap: '8px',
                            padding: '8px 20px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                padding: '8px',
                                gap: '8px',
                            }}
                        >
                            {arrayValues.map((contact) => (
                                <ContactChip
                                    key={contact.emailAddress}
                                    firstName={contact.firstName}
                                    lastName={contact.lastName}
                                    tooltipTitle={contact.emailAddress}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            ),
        }) as FormStructure;

    const createNonEditableArrayDataInput = ({
        label,
        name,
        arrayValues,
    }: NonEditableArrayDataType) =>
        ({
            type: 'customItem',
            name,
            renderField: () => (
                <Box sx={{ display: 'grid', gap: '4px' }}>
                    <Typography variant="l2dlabel">{label}</Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', padding: '8px', gap: '8px' }}>
                        {arrayValues.map((value, i) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                data-testid="mail-data-from-array"
                                sx={{
                                    gap: '8px',
                                    border: `1px solid ${palette.grey[400]}`,
                                    borderRadius: '12px',
                                    padding: '8px',
                                }}
                            >
                                {value}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ),
        }) as FormStructure;

    const createNonEditableSingleDataInput = ({ label, name, value }: NonEditableSingleDataType) =>
        ({
            type: 'customItem',
            name,
            renderField: () => (
                <Box sx={{ display: 'grid', gap: '4px' }}>
                    <Typography variant="l2dlabel">{label}</Typography>

                    <Box
                        sx={{
                            border: `${palette.grey[300]}  1px solid`,
                            borderRadius: '12px',
                            gap: '8px',
                            padding: '8px 20px',
                        }}
                    >
                        <Box
                            data-testid="mail-data-from-singleValue"
                            sx={{
                                gap: '8px',
                                borderRadius: '12px',
                                padding: '8px',
                                width: '100%',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {value}
                        </Box>
                    </Box>
                </Box>
            ),
        }) as FormStructure;

    return {
        createRecipientsInput,
        createNonEditableRecipientsDataInput,
        createNonEditableArrayDataInput,
        createNonEditableSingleDataInput,
    };
};

export default useMailInputsFormStructure;
